import React, { useState, useEffect, useCallback, useRef } from 'react';
import { T, FONTS, SHADOW } from '../../constants/theme.js';
import { fetchArrivals, fetchDepartures, fetchInHouse, checkIn, checkOut, markNoShow, createReservation, fetchRoomTypes, fetchRooms, fetchGuests } from '../../services/api.js';
import { Card, Btn, Tabs, Avatar, ResBadge, fmtDate, fmtCurrency, Modal, Field, Input, Select,
  Toast, SectionHeader, Empty, Spinner, OfflineBadge, useOfflineInvalidation } from '../../components/ui.jsx';

const MEAL_PLANS = ['room_only','bb','hb','fb','all_inclusive'];

/* ─── Inline Guest Search ───────────────────────────────────────────────── */
function GuestSearchInline({ pid, onSelect }) {
  const [query,    setQuery]   = useState('');
  const [results,  setResults] = useState([]);
  const [busy,     setBusy]    = useState(false);
  const [done,     setDone]    = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    if (!query || query.length < 2) { setResults([]); setDone(false); return; }
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      setBusy(true);
      try {
        const data = await fetchGuests(pid, query);
        const list = Array.isArray(data) ? data : (data?.content || []);
        setResults(list);
        setDone(true);
      } catch { setResults([]); setDone(true); }
      finally { setBusy(false); }
    }, 400);
  }, [query, pid]);

  return (
    <div style={{ marginBottom: 14 }}>
      <label style={{ display:'block', fontSize:10, fontWeight:700, color:T.muted,
        letterSpacing:.9, textTransform:'uppercase', marginBottom:5 }}>Search Existing Guest</label>
      <div style={{ position:'relative' }}>
        <input value={query} onChange={e => setQuery(e.target.value)}
          placeholder="Name or email…"
          style={{ width:'100%', border:`1.5px solid ${T.border}`, borderRadius:10,
            padding:'10px 13px', fontSize:13, fontFamily:FONTS.body, color:T.navy,
            outline:'none', boxSizing:'border-box' }} />
        {busy && (
          <div style={{ position:'absolute', right:12, top:'50%', transform:'translateY(-50%)',
            width:14, height:14, border:`2px solid ${T.border}`, borderTopColor:T.teal,
            borderRadius:'50%', animation:'spin 1s linear infinite' }} />
        )}
      </div>
      {query.length >= 2 && !busy && (
        <div style={{ marginTop:6, border:`1px solid ${T.border}`, borderRadius:10, overflow:'hidden',
          background:T.white, maxHeight:200, overflowY:'auto', boxShadow:SHADOW.md }}>
          {results.length === 0 && done ? (
            <p style={{ padding:'12px 14px', fontSize:12, color:T.muted }}>
              No registered guests found — fill details manually below
            </p>
          ) : results.slice(0,6).map(g => (
            <div key={g.id} onClick={() => { onSelect(g); setQuery(''); setResults([]); }}
              style={{ padding:'10px 14px', borderBottom:`1px solid ${T.border}`, cursor:'pointer',
                display:'flex', alignItems:'center', gap:10, transition:'background .15s' }}
              onMouseEnter={e => e.currentTarget.style.background = T.surface}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
              <div style={{ width:32, height:32, borderRadius:'50%', background:T.tealPale,
                display:'flex', alignItems:'center', justifyContent:'center',
                fontSize:12, fontWeight:700, color:T.teal, flexShrink:0 }}>
                {(g.name || g.firstName || '?')[0].toUpperCase()}
              </div>
              <div style={{ flex:1, minWidth:0 }}>
                <p style={{ fontSize:13, fontWeight:700, color:T.navy, margin:0,
                  overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                  {g.name || `${g.firstName||''} ${g.lastName||''}`.trim()}
                  {g.vipStatus && <span style={{ marginLeft:6, fontSize:10, color:T.gold }}>⭐</span>}
                </p>
                <p style={{ fontSize:11, color:T.muted, margin:0 }}>
                  {g.email}{g.phone ? ` · ${g.phone}` : ''}
                </p>
              </div>
              <span style={{ fontSize:11, color:T.teal, fontWeight:700 }}>Select</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function FrontDesk({ property, onNavigate }) {
  const [tab,        setTab]       = useState('arrivals');
  const [arrivals,   setArrivals]  = useState([]);
  const [departures, setDepartures]= useState([]);
  const [inhouse,    setInhouse]   = useState([]);
  const [loading,    setLoading]   = useState(true);
  const [actioning,  setActioning] = useState(null);
  const [toast,      setToast]     = useState('');
  const [walkIn,     setWalkIn]    = useState(false);
  const [rooms,      setRooms]     = useState([]);
  const [roomTypes,  setRoomTypes] = useState([]);

  // Walk-in form
  const [wi,           setWi]           = useState({ guestName:'', guestEmail:'', guestPhone:'', roomId:'', adults:1, children:0, mealPlan:'room_only', nights:1, ratePerNight:'', source:'walk_in' });
  const [selectedGuest, setSelectedGuest] = useState(null);

  const handleSelectWalkInGuest = (g) => {
    setSelectedGuest(g);
    setWi(p => ({
      ...p,
      guestName:  g.name || `${g.firstName||''} ${g.lastName||''}`.trim(),
      guestEmail: g.email  || '',
      guestPhone: g.phone  || '',
      guestId:    g.id,
    }));
  };

  const pid = property?.id;
  const currency = property?.currency || 'USD';

  const showToast = msg => { setToast(msg); setTimeout(() => setToast(''), 3500); };

  const load = useCallback(async () => {
    if (!pid) return;
    setLoading(true);
    // Use LOCAL date — toISOString() returns UTC which is one day behind in
    // UTC+ timezones between midnight and the UTC offset (e.g. 00:00–02:00
    // in UTC+2).  Reservation dates are stored in local date format so we
    // must compare using the local calendar date.
    const _n = new Date();
    const today = `${_n.getFullYear()}-${String(_n.getMonth()+1).padStart(2,'0')}-${String(_n.getDate()).padStart(2,'0')}`;
    const [a, d, ih] = await Promise.all([
      fetchArrivals(pid, today).catch(() => []),
      fetchDepartures(pid, today).catch(() => []),
      fetchInHouse(pid).catch(() => []),
    ]);
    setArrivals(Array.isArray(a) ? a : []);
    setDepartures(Array.isArray(d) ? d : []);
    setInhouse(Array.isArray(ih) ? ih : []);
    setLoading(false);
  }, [pid]);

  useOfflineInvalidation('reservations', load);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!walkIn || !pid) return;
    fetchRooms(pid).then(r => setRooms(Array.isArray(r) ? r.filter(rm => rm.status === 'available') : []));
    fetchRoomTypes(pid).then(rt => setRoomTypes(Array.isArray(rt) ? rt : []));
  }, [walkIn, pid]);

  const handleCheckIn = async (res, roomId) => {
    setActioning(res.id);
    try {
      const r = await checkIn(res.id, roomId || res.roomId);
      showToast(r?._offline ? `⏳ Check-in saved offline` : `✅ ${res.guestName} checked in`);
      load();
    } catch (e) { showToast(`❌ ${e.message}`); }
    finally { setActioning(null); }
  };

  const handleCheckOut = async (res) => {
    setActioning(res.id);
    try {
      const r = await checkOut(res.id);
      showToast(r?._offline ? `⏳ Check-out saved offline` : `✅ ${res.guestName} checked out`);
      load();
    } catch (e) { showToast(`❌ ${e.message}`); }
    finally { setActioning(null); }
  };

  const handleNoShow = async (res) => {
    setActioning(res.id);
    try {
      const r = await markNoShow(res.id);
      showToast(r?._offline ? `⏳ No-show queued` : `⚠️ ${res.guestName} marked as no-show`);
      load();
    } catch (e) { showToast(`❌ ${e.message}`); }
    finally { setActioning(null); }
  };

  const handleWalkIn = async () => {
    if (!wi.guestName || !wi.ratePerNight) { showToast('Name and rate required'); return; }
    const today = new Date();
    const dep = new Date(today);
    dep.setDate(dep.getDate() + parseInt(wi.nights || 1));
    const payload = {
      propertyId:    pid,
      guestId:       selectedGuest?.id || null,
      guestName:     wi.guestName,
      guestEmail:    wi.guestEmail,
      guestPhone:    wi.guestPhone,
      roomId:        wi.roomId || null,
      adults:        parseInt(wi.adults),
      children:      parseInt(wi.children),
      mealPlan:      wi.mealPlan,
      ratePerNight:  parseFloat(wi.ratePerNight),
      totalAmount:   parseFloat(wi.ratePerNight) * parseInt(wi.nights),
      currency,
      source:        'walk_in',
      status:        'checked_in',
      arrivalDate:   today.toISOString().slice(0,10),
      departureDate: dep.toISOString().slice(0,10),
      numNights:     parseInt(wi.nights),
    };
    try {
      const created = await createReservation(payload);
      showToast(created?._offline ? '⏳ Walk-in saved offline — will sync when server is back' : '✅ Walk-in checked in');
      setWalkIn(false);
      setSelectedGuest(null);
      setWi({ guestName:'', guestEmail:'', guestPhone:'', roomId:'', adults:1, children:0, mealPlan:'room_only', nights:1, ratePerNight:'', source:'walk_in' });
      load();
      if (created?.id) onNavigate('folio', created.id);
    } catch (e) { showToast(`❌ ${e.message}`); }
  };

  const TABS = [
    { id:'arrivals',   label:'Arrivals',   icon:'🛎️', count:arrivals.length },
    { id:'departures', label:'Departures', icon:'🚪', count:departures.length },
    { id:'inhouse',    label:'In-House',   icon:'🏠', count:inhouse.length },
  ];

  const list = tab === 'arrivals' ? arrivals : tab === 'departures' ? departures : inhouse;

  return (
    <div className="fade-up">
      <SectionHeader
        title="Front Desk"
        subtitle={`${new Date().toLocaleDateString('en-GB', { weekday:'long', day:'numeric', month:'long' })}`}
        right={<>
          <Btn variant="gold" onClick={() => setWalkIn(true)}>🚶 Walk-in</Btn>
          <Btn variant="primary" onClick={() => onNavigate('new-reservation')}>+ Reserve</Btn>
          <Btn variant="secondary" onClick={load}>↻</Btn>
        </>}
      />

      <Tabs tabs={TABS} active={tab} onSelect={setTab} />

      {loading ? (
        <div style={{ display:'flex', justifyContent:'center', padding:60 }}><Spinner /></div>
      ) : list.length === 0 ? (
        <Empty
          icon={tab === 'arrivals' ? '🛎️' : tab === 'departures' ? '🚪' : '🏠'}
          title={`No ${tab} today`}
          subtitle={tab === 'arrivals' ? 'All arrivals have been processed' : tab === 'departures' ? 'All departures done' : 'No guests currently in-house'}
        />
      ) : (
        <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
          {list.map(res => (
            <Card key={res.id} style={{ padding:'14px 18px' }}>
              <div style={{ display:'flex', alignItems:'flex-start', gap:14, flexWrap:'wrap' }}>
                <Avatar name={res.guestName} size={44} color={tab === 'arrivals' ? T.teal : tab === 'departures' ? T.warning : T.success} />
                <div style={{ flex:1, minWidth:180 }}>
                  <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
                    <p style={{ fontSize:15, fontWeight:700, color:T.navy }}>{res.guestName || '—'}</p>
                    <ResBadge status={res.status} />
                  </div>
                  <div style={{ display:'flex', gap:12, marginTop:4, flexWrap:'wrap' }}>
                    <span style={{ fontSize:11, color:T.muted }}>🔖 {res.confirmationNumber}</span>
                    {res.roomNumber && <span style={{ fontSize:11, color:T.muted }}>🚪 Room {res.roomNumber}</span>}
                    <span style={{ fontSize:11, color:T.muted }}>👥 {res.adults || 1} adult{res.adults !== 1 ? 's' : ''}{res.children > 0 ? `, ${res.children} child` : ''}</span>
                    <span style={{ fontSize:11, color:T.muted }}>📅 {fmtDate(res.arrivalDate)} → {fmtDate(res.departureDate)}</span>
                    {res.totalAmount && <span style={{ fontSize:11, color:T.muted }}>💰 {fmtCurrency(res.totalAmount, currency)}</span>}
                  </div>
                  {res.specialRequests && <p style={{ fontSize:11, color:T.gold, marginTop:4 }}>📝 {res.specialRequests}</p>}
                </div>
                <div className="r-card-actions">
                  {tab === 'arrivals' && res.status !== 'checked_in' && (
                    <>
                      <Btn size="sm" variant="primary" disabled={actioning === res.id} onClick={() => handleCheckIn(res)}>
                        {actioning === res.id ? '…' : '✓ Check In'}
                      </Btn>
                      <Btn size="sm" variant="secondary" disabled={actioning === res.id} onClick={() => handleNoShow(res)}>No-Show</Btn>
                    </>
                  )}
                  {tab === 'arrivals' && res.status === 'checked_in' && (
                    <span style={{ fontSize:11, color:T.success, fontWeight:700, padding:'4px 10px',
                      background:`${T.success}15`, borderRadius:8, border:`1px solid ${T.success}30` }}>
                      ✓ Checked In
                    </span>
                  )}
                  {tab === 'departures' && res.status !== 'checked_out' && (
                    <>
                      <Btn size="sm" variant="navy" disabled={actioning === res.id} onClick={() => handleCheckOut(res)}>
                        {actioning === res.id ? '…' : '✓ Check Out'}
                      </Btn>
                      <Btn size="sm" variant="ghost" onClick={() => onNavigate('folio', res.id)}>🧾 Folio</Btn>
                    </>
                  )}
                  {tab === 'departures' && res.status === 'checked_out' && (
                    <>
                      <span style={{ fontSize:11, color:T.muted, fontWeight:700, padding:'4px 10px',
                        background:`${T.border}40`, borderRadius:8, border:`1px solid ${T.border}` }}>
                        ✓ Checked Out
                      </span>
                      <Btn size="sm" variant="ghost" onClick={() => onNavigate('folio', res.id)}>🧾 Folio</Btn>
                    </>
                  )}
                  {tab === 'inhouse' && (
                    <>
                      <Btn size="sm" variant="ghost" onClick={() => onNavigate('folio', res.id)}>🧾 Folio</Btn>
                      <Btn size="sm" variant="secondary" onClick={() => onNavigate('reservation-detail', res.id)}>Detail</Btn>
                    </>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Walk-in modal */}
      <Modal open={walkIn} onClose={() => { setWalkIn(false); setSelectedGuest(null); }} title="🚶 Walk-in Check-In" width={640}>
        {/* Guest search */}
        <GuestSearchInline pid={pid} onSelect={handleSelectWalkInGuest} />

        {/* Selected guest badge */}
        {selectedGuest && (
          <div style={{ display:'flex', alignItems:'center', gap:12, padding:'10px 14px',
            background:T.tealPale, border:`1px solid ${T.teal}40`, borderRadius:10, marginBottom:14 }}>
            <div style={{ width:34, height:34, borderRadius:'50%', background:T.teal,
              display:'flex', alignItems:'center', justifyContent:'center',
              fontSize:13, fontWeight:700, color:T.white, flexShrink:0 }}>
              {wi.guestName[0]?.toUpperCase() || '?'}
            </div>
            <div style={{ flex:1 }}>
              <p style={{ fontSize:13, fontWeight:700, color:T.navy, margin:0 }}>{wi.guestName}</p>
              <p style={{ fontSize:11, color:T.teal, margin:0, fontWeight:600 }}>✓ Registered guest selected</p>
            </div>
            <button onClick={() => { setSelectedGuest(null); setWi(p => ({...p, guestName:'', guestEmail:'', guestPhone:''})); }}
              style={{ background:'none', border:`1px solid ${T.border}`, borderRadius:7,
                padding:'4px 10px', cursor:'pointer', fontSize:11, color:T.muted, fontFamily:FONTS.body }}>
              Clear
            </button>
          </div>
        )}

        <div style={{ borderTop:`1px solid ${T.border}`, paddingTop:14, marginBottom:14 }}>
          <p style={{ fontSize:10, fontWeight:700, color:T.muted, letterSpacing:.8, textTransform:'uppercase', marginBottom:10 }}>
            Guest Details
          </p>
        </div>

        <div className="r-walkin-grid">
          <div style={{ gridColumn:'1/-1' }}>
            <Field label="Guest Name" required>
              <Input value={wi.guestName} onChange={v => setWi(p => ({...p, guestName:v}))} placeholder="Full name" />
            </Field>
          </div>
          <Field label="Email">
            <Input type="email" value={wi.guestEmail} onChange={v => setWi(p => ({...p, guestEmail:v}))} placeholder="email@example.com" />
          </Field>
          <Field label="Phone">
            <Input value={wi.guestPhone} onChange={v => setWi(p => ({...p, guestPhone:v}))} placeholder="+250 7xx xxx xxx" />
          </Field>
          <Field label="Room">
            <Select value={wi.roomId} onChange={v => setWi(p => ({...p, roomId:v}))}>
              <option value="">— Any Available —</option>
              {rooms.map(r => <option key={r.id} value={r.id}>Room {r.roomNumber}{r.roomTypeName ? ` · ${r.roomTypeName}` : ''}</option>)}
            </Select>
          </Field>
          <Field label="Meal Plan">
            <Select value={wi.mealPlan} onChange={v => setWi(p => ({...p, mealPlan:v}))}>
              {MEAL_PLANS.map(m => <option key={m} value={m}>{m.replace(/_/g,' ').replace(/\b\w/g,l=>l.toUpperCase())}</option>)}
            </Select>
          </Field>
          <Field label="Nights" required>
            <Input type="number" min="1" value={wi.nights} onChange={v => setWi(p => ({...p, nights:v}))} />
          </Field>
          <Field label={`Rate / Night (${currency})`} required>
            <Input type="number" min="0" value={wi.ratePerNight} onChange={v => setWi(p => ({...p, ratePerNight:v}))} placeholder="0.00" />
          </Field>
          <Field label="Adults">
            <Input type="number" min="1" value={wi.adults} onChange={v => setWi(p => ({...p, adults:v}))} />
          </Field>
          <Field label="Children">
            <Input type="number" min="0" value={wi.children} onChange={v => setWi(p => ({...p, children:v}))} />
          </Field>
        </div>
        {wi.ratePerNight && wi.nights && (
          <div style={{ background:T.tealPale, borderRadius:10, padding:'12px 16px', marginTop:8,
            display:'flex', justifyContent:'space-between', alignItems:'center',
            border:`1px solid ${T.teal}30` }}>
            <div>
              <p style={{ fontSize:11, color:T.teal, fontWeight:700, margin:0 }}>Stay Total</p>
              <p style={{ fontSize:11, color:T.muted, margin:0 }}>{wi.nights} night{wi.nights>1?'s':''} × {fmtCurrency(parseFloat(wi.ratePerNight||0), currency)}</p>
            </div>
            <span style={{ fontSize:18, fontWeight:800, color:T.teal }}>{fmtCurrency(parseFloat(wi.ratePerNight||0)*parseInt(wi.nights||1), currency)}</span>
          </div>
        )}
        <div style={{ display:'flex', gap:10, marginTop:16 }}>
          <Btn variant="secondary" onClick={() => { setWalkIn(false); setSelectedGuest(null); }} style={{ flex:1 }}>Cancel</Btn>
          <Btn variant="primary" onClick={handleWalkIn} style={{ flex:2 }}>✓ Check In Walk-in</Btn>
        </div>
      </Modal>

      <Toast message={toast} />
    </div>
  );
}